import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { client } from "../../..";
import { WA_CLAIM, WA_MARK_READ, WA_RETRY, WA_SET_STATUS } from "../../../Queries/WhatsApp";
import {
  fetchWaStatus, fetchWaConversation, fetchWaMessages, upsertWaConversation, patchWaMessage, refreshWaMediaLinks,
} from "../../../Slices/whatsappSlice";
import ThreadMessages from "./ThreadMessages";
import Composer from "./Composer";
import SidePanel from "./SidePanel";
import TemplatePickerPanel from "./TemplatePickerPanel";
import QuickRepliesPanel from "./QuickRepliesPanel";
import { displayNameOf, gqlErrorMessage, initialsOf, isWindowOpen, windowRemaining } from "./waUtils";

const CLOCK_TICK_MS = 30 * 1000;

/**
 * One live conversation: header, messages, composer and its side panels
 * (templates, quick replies, image viewer). Shared by the inbox and the
 * WhatsApp tab on lead/client pages — both read the same Redux state, so a
 * message sent or received in one shows up in the other immediately.
 *
 * Must be rendered inside a `.wa-root` (side panels position against it).
 *
 * Props:
 *   conversationId  conversation to show
 *   onBack          shows a back arrow (inbox list navigation)
 *   headerActions   extra header buttons (e.g. inbox details toggle, "Open in Inbox")
 *   onPanelChange   (isOpen) → lets the host suspend its own shortcuts
 */
const ConversationPane = ({ conversationId, onBack, headerActions, onPanelChange }) => {
  const dispatch = useDispatch();
  const { status, details, messages, list, quickReplies } = useSelector((s) => s.whatsapp);
  const [now, setNow] = useState(Date.now());
  const [panel, setPanelState] = useState(null); // { type: "template" | "quick" | "image", ... }
  const [insertRequest, setInsertRequest] = useState(null);
  const [threadError, setThreadError] = useState(null);
  const markingRef = useRef(null);

  const setPanel = useCallback((p) => {
    setPanelState(p);
    onPanelChange?.(!!p);
  }, [onPanelChange]);

  const detail = conversationId ? details[conversationId] : null;
  const listItem = list.items.find((c) => c.id === conversationId);
  // List items and detail copies are both kept live by sockets; prefer the list copy.
  const conversation = listItem || detail?.conversation || null;
  const thread = conversationId ? messages[conversationId] : null;
  const canAct = detail?.permissions?.canAct ?? false;

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => clearInterval(t);
  }, []);

  // Unmounting with a panel open must not leave the host thinking one is still open.
  useEffect(() => () => onPanelChange?.(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load (or reload) whenever the conversation changes.
  useEffect(() => {
    if (!conversationId) return;
    setThreadError(null);
    setPanel(null);
    dispatch(fetchWaConversation(conversationId));
    dispatch(fetchWaMessages({ conversationId }));
  }, [dispatch, conversationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark read whenever the open conversation has unread messages (incl. live arrivals).
  useEffect(() => {
    if (!conversation || !conversation.unreadCount) return;
    if (conversation.assignedTo && conversation.assignedTo !== status?.userId) return; // don't clear a teammate's badge
    if (document.visibilityState !== "visible" || markingRef.current === conversation.id) return;
    markingRef.current = conversation.id;
    client.mutate({ mutation: WA_MARK_READ, variables: { id: conversation.id } })
      .then(({ data }) => { dispatch(upsertWaConversation(data.whatsappMarkRead)); dispatch(fetchWaStatus()); })
      .catch(() => {})
      .finally(() => { markingRef.current = null; });
  }, [conversation?.id, conversation?.unreadCount, conversation?.assignedTo, status?.userId, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadOlder = useCallback(() => {
    if (!thread?.items?.length || thread.loading) return;
    dispatch(fetchWaMessages({ conversationId, before: thread.items[0].timestamp }));
  }, [dispatch, conversationId, thread]);

  const refreshMedia = useCallback((ids) => dispatch(refreshWaMediaLinks(ids)), [dispatch]);

  const retry = async (message) => {
    try {
      const { data } = await client.mutate({ mutation: WA_RETRY, variables: { messageId: message.id } });
      dispatch(patchWaMessage(data.whatsappRetryMessage));
    } catch (err) {
      setThreadError(gqlErrorMessage(err));
    }
  };

  const quickAction = async (mutation, variables, pick) => {
    setThreadError(null);
    try {
      const { data } = await client.mutate({ mutation, variables });
      dispatch(upsertWaConversation(pick(data)));
      dispatch(fetchWaConversation(variables.id));
      dispatch(fetchWaStatus());
    } catch (err) {
      setThreadError(gqlErrorMessage(err));
    }
  };

  const fillContext = useMemo(() => ({
    contactName: detail?.linked?.lead?.contact?.name || conversation?.contactName || conversation?.profileName || "",
    recordName: detail?.linked?.lead?.agencyName || detail?.linked?.client?.clientName || "",
    senderName: status?.userName || "",
  }), [detail, conversation, status?.userName]);

  const compliance = detail?.consent
    ? { optInStatus: detail.consent.optInStatus, hasPriorInbound: !!conversation?.lastInboundAt, emailEngaged: detail.emailEngaged, emailEngagementRequired: detail.emailEngagementRequired !== false }
    : null;

  if (!conversation) {
    if (detail?.error) {
      return (
        <div className="wa-thread" role="region" aria-label="Conversation">
          <div className="wa-empty">
            <i className="fa-solid fa-triangle-exclamation wa-empty__icon" />
            <div className="wa-empty__title">Couldn't open this conversation</div>
            <div>{detail.error}</div>
            <button type="button" className="wa-btn wa-btn--sm" onClick={() => dispatch(fetchWaConversation(conversationId))}>Retry</button>
          </div>
        </div>
      );
    }
    return (
      <div className="wa-thread" role="region" aria-label="Conversation">
        <div className="wa-empty"><i className="fa-solid fa-spinner wa-spin wa-empty__icon" /> Loading conversation…</div>
      </div>
    );
  }

  const owner = conversation.assignedTo;
  const ownerIsMe = owner && owner === status?.userId;
  const remaining = windowRemaining(conversation.windowExpiresAt, now);
  const unreachable = detail?.consent?.reachability?.status === "no";

  return (
    <>
      <div className="wa-thread" role="region" aria-label="Conversation">
        <header className="wa-thread__head">
          {onBack && (
            <button type="button" className="wa-icon-btn" onClick={onBack} aria-label="Back to list" title="Back">
              <i className="fa-solid fa-arrow-left" />
            </button>
          )}
          <div className="wa-avatar">{initialsOf(displayNameOf(conversation), "#")}</div>
          <div className="wa-thread__who">
            <div className="wa-thread__name">{displayNameOf(conversation)}</div>
            <div className="wa-thread__sub">
              <span>{conversation.displayNumber}</span>
              <span className={`wa-chip ${isWindowOpen(conversation, now) ? "wa-chip--ok" : ""}`}>
                <span className={`wa-dot ${isWindowOpen(conversation, now) ? "wa-dot--open" : ""}`} />
                {remaining ? `Window open · ${remaining}` : "Templates only"}
              </span>
              {unreachable && (
                <span className="wa-chip wa-chip--danger" title="Meta couldn't deliver to this number — it may not be on WhatsApp.">
                  <i className="fa-solid fa-circle-exclamation" /> Likely not on WhatsApp
                </span>
              )}
              {detail?.linked?.lead && <span className="wa-chip wa-chip--accent">{detail.linked.lead.agencyName}</span>}
              {!detail?.linked?.lead && detail?.linked?.client && <span className="wa-chip wa-chip--accent">{detail.linked.client.clientName}</span>}
            </div>
          </div>
          <div className="wa-thread__actions">
            {!owner && (
              <button type="button" className="wa-btn wa-btn--sm wa-btn--primary" onClick={() => quickAction(WA_CLAIM, { id: conversation.id }, (d) => d.whatsappClaimConversation)}>
                <i className="fa-solid fa-hand" /> Claim
              </button>
            )}
            {owner && !ownerIsMe && <span className="wa-chip"><i className="fa-solid fa-user" /> {conversation.assignedToName}</span>}
            {canAct && conversation.status !== "closed" && (
              <button type="button" className="wa-btn wa-btn--sm" onClick={() => quickAction(WA_SET_STATUS, { id: conversation.id, status: "closed" }, (d) => d.whatsappSetConversationStatus)} title="Close conversation">
                <i className="fa-solid fa-check" /> Close
              </button>
            )}
            {canAct && conversation.status === "closed" && (
              <button type="button" className="wa-btn wa-btn--sm" onClick={() => quickAction(WA_SET_STATUS, { id: conversation.id, status: "open" }, (d) => d.whatsappSetConversationStatus)}>
                <i className="fa-solid fa-rotate-left" /> Reopen
              </button>
            )}
            {headerActions}
          </div>
        </header>

        {threadError && (
          <div className="wa-alert wa-alert--danger" style={{ margin: "8px 16px 0" }} role="alert">
            <i className="fa-solid fa-circle-exclamation" /> <span style={{ flex: 1 }}>{threadError}</span>
            <button type="button" className="wa-icon-btn" onClick={() => setThreadError(null)} aria-label="Dismiss"><i className="fa-solid fa-xmark" /></button>
          </div>
        )}
        {thread?.error && !thread.items.length ? (
          <div className="wa-empty">
            <i className="fa-solid fa-triangle-exclamation wa-empty__icon" />
            <div className="wa-empty__title">Couldn't load messages</div>
            <div>{thread.error}</div>
            <button type="button" className="wa-btn wa-btn--sm" onClick={() => dispatch(fetchWaMessages({ conversationId }))}>Retry</button>
          </div>
        ) : thread?.loading && !thread.items.length ? (
          <div className="wa-empty"><i className="fa-solid fa-spinner wa-spin wa-empty__icon" /> Loading messages…</div>
        ) : (
          <ThreadMessages
            conversationKey={conversationId}
            items={thread?.items || []}
            hasMore={thread?.hasMore}
            loading={thread?.loading}
            onLoadOlder={loadOlder}
            onImageClick={(url, m) => setPanel({ type: "image", url, caption: m.body })}
            onRetry={retry}
            canRetry={canAct}
            onRefreshMedia={refreshMedia}
          />
        )}

        {detail && !detail.error && (
          <Composer
            conversation={conversation}
            canAct={canAct}
            ownerName={conversation.assignedToName}
            quickReplies={quickReplies}
            maxTextLength={status?.limits?.maxTextLength || 4096}
            maxCaptionLength={status?.limits?.maxCaptionLength || 1024}
            onOpenTemplates={() => setPanel({ type: "template" })}
            onOpenQuickReplies={() => setPanel({ type: "quick" })}
            insertRequest={insertRequest}
            now={now}
          />
        )}
      </div>

      {panel?.type === "template" && (
        <TemplatePickerPanel conversation={conversation} fillContext={fillContext} compliance={compliance} onClose={() => setPanel(null)} />
      )}
      {panel?.type === "quick" && (
        <QuickRepliesPanel
          onClose={() => setPanel(null)}
          onInsert={canAct && isWindowOpen(conversation, now)
            ? (reply) => { setInsertRequest({ reply, nonce: Date.now() }); setPanel(null); }
            : null}
        />
      )}
      {panel?.type === "image" && (
        <SidePanel wide title="Image" subtitle={panel.caption || undefined} onClose={() => setPanel(null)}
          footer={<a className="wa-btn" href={`${panel.url}&download=1`} target="_blank" rel="noopener noreferrer"><i className="fa-solid fa-download" /> Download</a>}>
          <div className="wa-viewer"><img src={panel.url} alt={panel.caption || "WhatsApp image"} /></div>
        </SidePanel>
      )}
    </>
  );
};

export default ConversationPane;
